import { MultipleSelectComponent } from 'src/app/components/multiple-select/multiple-select.component';
import {
    IQueryContext,
    IQueryContextDatasourceColumn,
} from 'src/app/services/query-context/models/IQueryContext';
import { BQSqlFunctionHelper } from 'src/app/utils/datasource-sql-map/bq-sql-function-helper';
import { DatePickerSqlTokensCompiler } from 'src/app/utils/datasource-sql-map/date-picker-sql-tokens-compiler';
import {
    multipleSelectComponentCompileSQLTokens,
    segmentContainerIdCompileSQLTokens,
} from 'src/app/utils/datasource-sql-map/utils';
import { GcpProjectId } from 'src/app/utils/gcp-project-id.model';
import { buildSplitGroupSQL, buildWhereSQL } from 'src/app/utils/query/sql';
import { sqlDateTrunc } from 'src/app/utils/sql';
import {
    ICompiledSQLTokens,
    IDatasourceSQLMap,
    mergeSqlTokensArray,
} from '../../../../utils/datasource-sql-map/datasource';

export class BiVizDashboardRetention {
    public static sqlMap: IDatasourceSQLMap = {
        dataset: 'bi_viz',
        tableId: 'dashboard_retention',
        columns: {
            FIREBASE_FIRST_OPEN_DATE: {
                columnId: 'FIREBASE_FIRST_OPEN_DATE',
                columnSQL: 'first_session_date',
                compileSQLTokens: (state) => {
                    return DatePickerSqlTokensCompiler.compileTokens(
                        'first_session_date',
                        state
                    );
                },
            },
            FIREBASE_APP_STORE: {
                columnId: 'FIREBASE_APP_STORE',
                columnSQL: 'player_dim.revenue_sessions.first_store',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'player_dim.revenue_sessions.first_store',
                        state
                    );
                },
            },
            FIREBASE_COUNTRY_EUROPEAN_UNION: {
                columnId: 'FIREBASE_COUNTRY_EUROPEAN_UNION',
                columnSQL: 'player_dim.geo.first_geo.country_european_union',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'player_dim.geo.first_geo.country_european_union',
                        state
                    );
                },
            },
            FIREBASE_COUNTRY_TIER: {
                columnId: 'FIREBASE_COUNTRY_TIER',
                columnSQL: 'player_dim.geo.first_geo.country_tier_label',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'player_dim.geo.first_geo.country_tier_label',
                        state
                    );
                },
            },
            FIREBASE_COUNTRY: {
                columnId: 'FIREBASE_COUNTRY',
                columnSQL: 'player_dim.geo.first_geo.unified_country',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'player_dim.geo.first_geo.unified_country',
                        state
                    );
                },
            },
            FIREBASE_COUNTRY_US_REGION: {
                columnId: 'FIREBASE_COUNTRY_US_REGION',
                columnSQL: 'player_dim.geo.first_geo.us_subdivision',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'player_dim.geo.first_geo.us_subdivision',
                        state
                    );
                },
            },
            ETL_PAYER: {
                columnId: 'ETL_PAYER',
                columnSQL: 'player_dim.revenue_sessions.payer_label',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'player_dim.revenue_sessions.payer_label',
                        state
                    );
                },
            },
            ETL_REVENUE_BADGE: {
                columnId: 'ETL_REVENUE_BADGE',
                columnSQL: 'player_dim.revenue_sessions.revenue_badge',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'player_dim.revenue_sessions.revenue_badge',
                        state
                    );
                },
            },
            FIREBASE_INSTALL_VERSION: {
                columnId: 'FIREBASE_INSTALL_VERSION',
                columnSQL: 'player_dim.revenue_sessions.install_version',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'player_dim.revenue_sessions.install_version',
                        state
                    );
                },
            },
            FIREBASE_INSTALL_VERSION_GROUP_MINOR: {
                columnId: 'FIREBASE_INSTALL_VERSION_GROUP_MINOR',
                columnSQL: `${BQSqlFunctionHelper.getCallSqlStatement(
                    BQSqlFunctionHelper.VERSION_HIERARCHY,
                    ['player_dim.revenue_sessions.install_version']
                )}.minor`,
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        `${BQSqlFunctionHelper.getCallSqlStatement(
                            BQSqlFunctionHelper.VERSION_HIERARCHY,
                            ['player_dim.revenue_sessions.install_version']
                        )}.minor`,
                        state
                    );
                },
            },
            ATTRIBUTION_TRAFFIC_SOURCE: {
                columnId: 'ATTRIBUTION_TRAFFIC_SOURCE',
                columnSQL: 'player_dim.attribution.unified_traffic',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'player_dim.attribution.unified_traffic',
                        state
                    );
                },
            },
            ATTRIBUTION_NETWORK_NAME: {
                columnId: 'ATTRIBUTION_NETWORK_NAME',
                columnSQL: 'player_dim.attribution.unified_network',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'player_dim.attribution.unified_network',
                        state
                    );
                },
            },
            ATTRIBUTION_CAMPAIGN_ACTIVE: {
                columnId: 'ATTRIBUTION_CAMPAIGN_ACTIVE',
                columnSQL: 'active_campaign',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'player_dim.attribution.active_campaign',
                        state,
                        MultipleSelectComponent.toBooleanSQL
                    );
                },
            },
            ATTRIBUTION_CAMPAIGN_NAME: {
                columnId: 'ATTRIBUTION_CAMPAIGN_NAME',
                columnSQL: 'player_dim.attribution.unified_campaign',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'player_dim.attribution.unified_campaign',
                        state
                    );
                },
            },
            ATTRIBUTION_AD: {
                columnId: 'ATTRIBUTION_AD',
                columnSQL: 'player_dim.attribution.unified_ad',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'player_dim.attribution.unified_ad',
                        state
                    );
                },
            },
            ATTRIBUTION_AD_LENGTH_S: {
                columnId: 'ATTRIBUTION_AD_LENGTH_S',
                columnSQL: 'player_dim.attribution.unified_ad_dim.length_s',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'player_dim.attribution.unified_ad_dim.length_s',
                        state
                    );
                },
            },
            ATTRIBUTION_AD_SIZE: {
                columnId: 'ATTRIBUTION_AD_SIZE',
                columnSQL: 'player_dim.attribution.unified_ad_dim.size',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'player_dim.attribution.unified_ad_dim.size',
                        state
                    );
                },
            },
            ATTRIBUTION_AD_ASPECT_RATIO: {
                columnId: 'ATTRIBUTION_AD_ASPECT_RATIO',
                columnSQL: 'player_dim.attribution.unified_ad_dim.aspect_ratio',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'player_dim.attribution.unified_ad_dim.aspect_ratio',
                        state
                    );
                },
            },
            ATTRIBUTION_AD_TYPE: {
                columnId: 'ATTRIBUTION_AD_TYPE',
                columnSQL: 'player_dim.attribution.unified_ad_dim.type',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'player_dim.attribution.unified_ad_dim.type',
                        state
                    );
                },
            },
            ATTRIBUTION_AD_THEME: {
                columnId: 'ATTRIBUTION_AD_THEME',
                columnSQL: 'player_dim.attribution.unified_ad_dim.theme',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'player_dim.attribution.unified_ad_dim.theme',
                        state
                    );
                },
            },
            ATTRIBUTION_AD_VERSION: {
                columnId: 'ATTRIBUTION_AD_VERSION',
                columnSQL: 'player_dim.attribution.unified_ad_dim.version',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'player_dim.attribution.unified_ad_dim.version',
                        state
                    );
                },
            },
            SEGMENT: {
                columnId: 'SEGMENT',
                columnSQL: 'segments',
                compileSQLTokens: segmentContainerIdCompileSQLTokens,
            },
            SEGMENT_EXCLUDE: {
                columnId: 'SEGMENT_EXCLUDE',
                columnSQL: 'segments',
                compileSQLTokens: segmentContainerIdCompileSQLTokens,
            },
        },
    };

    // compile sql statements specific for this datasource
    public static compileSQLStatements(
        supportedDatasourceColumns: IQueryContextDatasourceColumn[],
        queryContextSettings: IQueryContext['settings'],
        options: { gcpProjectId: GcpProjectId },
        customCompiledSqlTokens?: Partial<ICompiledSQLTokens>
    ) {
        const dateTrunc = queryContextSettings.dateAggregation
            ? sqlDateTrunc(
                  'first_session_date',
                  queryContextSettings.dateAggregation
              )
            : `first_session_date`;

        // if app_version is present in QueryContext
        // const is_in_context_app_version =
        //     supportedDatasourceColumns.findIndex(
        //         (dsCol) => dsCol.id === 'ACTIVITY_APP_VERSION'
        //     ) != -1;

        // if segment_container_id is present in splits or filters
        const is_in_context_segment =
            supportedDatasourceColumns.findIndex(
                (dsCol) => dsCol.id === 'SEGMENT'
            ) != -1;

        const sqlTokensArray: Partial<ICompiledSQLTokens>[] =
            supportedDatasourceColumns.map((dsCol) => {
                return this.sqlMap.columns[dsCol.id]!.compileSQLTokens!(
                    dsCol.state,
                    options
                );
            });

        // merge tokens into single deduplicated object of type ICompiledSQLTokens
        const sqlTokens: ICompiledSQLTokens =
            mergeSqlTokensArray(sqlTokensArray);

        // unnest app_version_activity if app_version is present in splits or filters
        // TODO to powinno być rozwiązane na poziomie datasource, gdzie compileSQL zwróci atrybut join, z treścią `, UNNEST...`

        const splitSelect: string = buildSplitGroupSQL(sqlTokens.split_group);
        const splitGroupName = 'split_group';
        const splitGroupBy = splitSelect ? `${splitGroupName}, ` : '';
        // const countStarOrDistinctProperId = splitSelect
        //     ? 'COUNT(DISTINCT proper_id)'
        //     : 'COUNT(*)';
        // const unnestDatasourceColumn =
        //     sqlTokens.unnestDatasourceColumn.join('\n');
        const where: string = buildWhereSQL([
            ...sqlTokens.where,
            ...(customCompiledSqlTokens?.where ?? []),
        ]);
        const join: string = sqlTokens.join.join('\n');
        // const windowTotal = splitGroupBy ? 'PARTITION BY split_group' : '';
        const windowRunning = splitGroupBy
            ? 'PARTITION BY split_group ORDER BY day_no'
            : 'ORDER BY day_no';

        return {
            dateTrunc,
            splitSelect,
            splitGroupName,
            splitGroupBy,
            where,
            join,
            windowRunning,
        };
    }
}
